import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, FolderOpen, Laptop, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { useFetcher } from "~/hooks/useFetcher";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { useProjectFilesTarget } from "~/pages/manifold-nextgen/components/projects/useProjectFilesTarget";
import {
  artifactMethodsFormatsPath,
  type ArtifactMethodsFormats,
  type ArtifactType,
  type ConvertAndCopyFormat,
  type ProjectFile,
} from "~/services/artifacts";
import { pinnedArtifactMethodsFormatsPath } from "~/services/pinnedArtifacts";
import { defaultFormatFor, formatOptionsFor, METHODS_OPTIONS, type MethodsOption } from "./formats";
import { useArtifactActions, useCanDownloadData } from "./useArtifactActions";

type Stage = "select" | "working" | "saved" | "error";
type Choice = ConvertAndCopyFormat | MethodsOption["value"];

const Destination = {
  COMPUTER: "computer",
  PROJECT: "project",
} as const;
type Destination = (typeof Destination)[keyof typeof Destination];

interface DownloadDialogProps {
  open: boolean;
  onClose: () => void;
  // Needed to download from a session; a pin in scope downloads without it.
  artifactId?: string;
  artifactType: ArtifactType;
}

// Modal flow for downloading an artifact, or saving it to the session's
// project files. Pick a destination (only inside a project with files) and a
// format, show a spinner while the convert-and-copy Lambda runs, recover from
// errors. The action waits while the project files check is pending, so the
// destination choice can't appear under a click already in flight.
//
// With a pin in scope the dialog downloads through the pin, for a reader who
// can't open the session the artifact is in.
export const DownloadDialog = ({
  open,
  onClose,
  artifactId,
  artifactType,
}: DownloadDialogProps) => {
  const actions = useArtifactActions(artifactId, artifactType);
  const { conversationId, pin } = useConversationContext();
  // Saving through a pin isn't supported, so a pin only downloads, and the
  // project files check is skipped.
  const projectFiles = useProjectFilesTarget(!pin);
  // The notebook is offered only once the server says this workflow can be
  // one; the Markdown document is always available.
  const methodsFormatsPath = pin
    ? pinnedArtifactMethodsFormatsPath(pin)
    : conversationId && artifactId && artifactMethodsFormatsPath(conversationId, artifactId);
  const { data: methodsFormats } = useFetcher<ArtifactMethodsFormats>(
    open && methodsFormatsPath ? methodsFormatsPath : null
  );
  // A caller without the data download scope is offered the methods document
  // alone, rather than a format the server would refuse.
  const canDownloadData = useCanDownloadData();
  const options = [
    ...(canDownloadData ? formatOptionsFor(artifactType) : []),
    ...METHODS_OPTIONS.filter(
      (o) => o.documentFormat === "markdown" || methodsFormats?.formats.includes(o.documentFormat)
    ),
  ];
  const defaultChoice: Choice = canDownloadData
    ? defaultFormatFor(artifactType)
    : METHODS_OPTIONS[0].value;
  const [stage, setStage] = useState<Stage>("select");
  // Project files is the default; without them the dialog only downloads, so
  // the choice falls back to the computer on its own.
  const [destination, setDestination] = useState<Destination>(Destination.PROJECT);
  const [format, setFormat] = useState<Choice>(defaultChoice);
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState<ProjectFile | undefined>();

  useEffect(() => {
    if (open) {
      setStage("select");
      setDestination(Destination.PROJECT);
      setFormat(defaultChoice);
      setError(undefined);
      setSaved(undefined);
    }
  }, [open, defaultChoice]);

  const pending = projectFiles === undefined;
  const toProject = !!projectFiles && destination === Destination.PROJECT;

  const handleSubmit = async () => {
    setStage("working");
    const methods = METHODS_OPTIONS.find((o) => o.value === format);
    if (toProject) {
      const result = methods
        ? await actions.saveMethodsDocumentToProject(methods.documentFormat)
        : await actions.saveToProject(format as ConvertAndCopyFormat);
      if (result.ok) {
        setSaved(result.file);
        setStage("saved");
      } else {
        setError(result.error);
        setStage("error");
      }
      return;
    }
    const result = methods
      ? await actions.downloadMethodsDocument(methods.documentFormat)
      : await actions.download(format as ConvertAndCopyFormat);
    if (result.ok) {
      onClose();
    } else {
      setError(result.error);
      setStage("error");
    }
  };

  const handleRetry = () => {
    setError(undefined);
    setStage("select");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        {stage === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>Download artifact</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 py-4">
              {projectFiles && (
                <div>
                  <p className="mb-2 text-sm font-medium">Save to</p>
                  <RadioGroup
                    value={destination}
                    onValueChange={(v) => setDestination(v as Destination)}
                    className="grid-cols-2 gap-2"
                  >
                    <RadioGroupItem
                      value={Destination.COMPUTER}
                      label={
                        <DestinationLabel
                          icon={<Laptop className="size-4" />}
                          text="Your computer"
                        />
                      }
                      description="Downloads through your browser"
                      variant="card"
                      className="w-full"
                    />
                    <RadioGroupItem
                      value={Destination.PROJECT}
                      label={
                        <DestinationLabel
                          icon={<FolderOpen className="size-4" />}
                          text="Project files"
                        />
                      }
                      description="Saved to the project's exports folder"
                      variant="card"
                      className="w-full"
                    />
                  </RadioGroup>
                </div>
              )}
              <div>
                <p className="mb-4 text-sm text-gray-600">Choose a format for your download:</p>
                <RadioGroup value={format} onValueChange={(v) => setFormat(v as Choice)}>
                  {options.map((opt) => (
                    <RadioGroupItem
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                      description={opt.description}
                      variant="card"
                      className="w-full"
                    />
                  ))}
                </RadioGroup>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={pending}>
                {toProject ? "Save to Files" : "Download"}
              </Button>
            </div>
          </>
        )}

        {stage === "working" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Loader2 className="size-10 animate-spin text-primary" />
            <p className="text-base font-medium">
              {toProject ? "Saving to project files…" : "Preparing download…"}
            </p>
            <p className="text-sm text-gray-500">This may take a moment for large files.</p>
          </div>
        )}

        {stage === "saved" && saved && projectFiles && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="rounded-full bg-green-100 p-5">
              <CheckCircle2 className="size-8 text-green-600" />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-lg font-bold">Saved to project files</p>
              <p className="text-sm text-gray-600">
                {saved.file_name} is in the project&apos;s exports folder.
              </p>
            </div>
            <div className="mt-2 flex flex-col items-center gap-1">
              <Button asChild className="px-6">
                <Link to={projectFiles.exportsPath}>Open project files</Link>
              </Button>
              <Button variant="link" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        )}

        {stage === "error" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="rounded-full bg-red-100 p-5">
              <AlertCircle className="size-8 text-red-600" />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-lg font-bold">
                {toProject ? "Unable to save to project files" : "Unable to download artifact"}
              </p>
              <p className="text-sm text-gray-600">
                {error || "Something went wrong while downloading your file."}
              </p>
            </div>
            <div className="mt-2 flex flex-col items-center gap-1">
              <Button onClick={handleRetry} className="px-6">
                Retry
              </Button>
              <Button variant="link" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DestinationLabel = ({ icon, text }: { icon: ReactNode; text: string }) => (
  <span className="flex items-center gap-1.5 text-sm font-medium leading-none text-foreground">
    {icon}
    {text}
  </span>
);
